///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { LinkFeature } from '../../logical-topology/entities/link/link-feature';
import {
    BomConfigDTO,
    BomDeviceDTO,
    BomDTO,
    BomNodeDTO,
    EngineAntennaTypeEnum,
    ExtendedProductdata,
    GtthKeyValueDTO,
    ProductAccessoryTypeEnum,
    ProductDeviceTypeEnum,
    ProductMhTypeEnum,
    ProductRadioDTO
} from '../../../generated/shared-types.model';
import { ProjectProductsService } from '../../logical-topology/engine/project-products-service';
import { GtthReportsTabs } from '../reports/reports-tabs.enum';
import { GtthSetProjectBomCommand } from '../../logical-topology/engine/commands/bom/set-project-bom-command';
import { ExtendedBomDeviceDTO } from './dto/extended-bom-device-dto.model';
import { BomDeviceConfigModel } from './dto/bom-device-config.model';
import { BomReportUtils } from './bom-report-utils';
import { Observable, Subject } from 'rxjs';
import { GtthProjectStateService } from '../../logical-topology/engine/project-state.service';
import { LinkProduct } from '../../logical-topology/entities/link/link-product';
import { DeviceIpViewDataModel } from './dto/device-ip-view-data.model';
import { GtthGetProjectBomCommand } from '../../logical-topology/engine/commands/bom/get-project-bom-command';
import { isDefined } from '@siklu/sui-lib/utils';
import { TopologyLinkTypeEnum } from '../../../services/project-socket/dto/topology-types.model';

@Injectable({
    providedIn: 'root'
})
export class GtthBomReportService {
    private devicesMap: Map<string, ExtendedBomDeviceDTO[]>;
    private defaultConfig: ExtendedBomDeviceDTO[];

    private readonly linksStateSubject: Subject<string[]>;

    constructor(
        private readonly projectProductsService: ProjectProductsService,
        private readonly projectStateService: GtthProjectStateService
    ) {
        this.linksStateSubject = new Subject();
    }

    private _historyService: GtthHistoryService;

    set historyService(value: GtthHistoryService) {
        this._historyService = value;
    }

    subscribeToLinksChanges(): Observable<string[]> {
        return this.linksStateSubject.asObservable();
    }

    isBomExist(): boolean {
        return this.devicesMap?.size > 0;
    }

    setBom(bomConfig: BomConfigDTO) {
        if (bomConfig) {
            this.devicesMap = BomReportUtils.convertBomToMap(bomConfig.bomDTO);
            this.defaultConfig = bomConfig.defaultConfig
                ? bomConfig.defaultConfig.map((device) => ExtendedBomDeviceDTO.fromDeviceDto(device))
                : [];
        } else {
            this.devicesMap = new Map<string, ExtendedBomDeviceDTO[]>();
            this.defaultConfig = [];
        }
    }

    async updateBom(save = false) {
        const forceProductsReload = !this.devicesMap || this.devicesMap.size === 0;

        await this.projectProductsService.loadProducts(this.projectStateService.getProjectId(), forceProductsReload);

        const getBomCommand = this._historyService.command(GtthGetProjectBomCommand);
        await this._historyService.doCommandBypassHistory(getBomCommand);
        const bomFromServer = getBomCommand.bomConfig;
        const newNodesMap = BomReportUtils.convertBomToMap(bomFromServer.bomDTO);
        this.defaultConfig = bomFromServer.defaultConfig
            ? bomFromServer.defaultConfig.map((device) => ExtendedBomDeviceDTO.fromDeviceDto(device))
            : [];

        this.checkMhDevicesCollocation(newNodesMap);

        newNodesMap.forEach((devices: BomDeviceDTO[], name: string) => {
            devices.forEach((device: BomDeviceDTO) => {
                if (device.typeProductId) {
                    const radio = this.projectProductsService.getRadioById(device.typeProductId);

                    if (radio && radio.name === device.typeName) {
                        return true;
                    } else if (device.typeName) {
                        const radioByName = this.projectProductsService.getRadioByName(device.typeName);
                        if (radioByName) {
                            device.typeProductId = radioByName.id;
                            return true;
                        } else {
                            device.typeProductId = undefined;
                        }
                    }
                }

                const radios = this.projectProductsService.getBomRadios(device.type, device.isSectorBu, device.deviceType);
                let predefinedValue;

                if (this.projectProductsService.isMh(device.type)) {
                    if (!device.isSectorBu) {
                        const linkName = `${name}-${device.secondNodeName}`;
                        const predefinedProduct = this._historyService.logicalTopology.predefined_devices_products;
                        if (predefinedProduct?.[linkName]) {
                            predefinedValue = radios.find(
                                (keyValue: GtthKeyValueDTO) => keyValue.value.indexOf(predefinedProduct[linkName]) > -1
                            );
                        } else {
                            predefinedValue = radios.find(
                                (keyValue: GtthKeyValueDTO) =>
                                    keyValue.key === this._historyService.logicalTopology.defaultTuProductPerLayer[device.layerIndex]
                            );
                        }
                    } else {
                        predefinedValue = radios.find(
                            (keyValue: GtthKeyValueDTO) =>
                                keyValue.key === this._historyService.logicalTopology.defaultBuProductPerLayer[device.layerIndex]
                        );
                    }
                }

                if (predefinedValue) {
                    device.typeProductId = predefinedValue.key; // check for undefined, bug report from mail
                    device.typeName = predefinedValue.value;
                } else {
                    device.typeProductId = radios[0].key; // check for undefined, bug report from mail
                    device.typeName = radios[0].value;
                }
            });
        });

        this.devicesMap = newNodesMap;

        if (save) {
            this.saveBom();
        }
    }

    triggerLinkState(fromStationName, toStationName): void {
        this.linksStateSubject.next([fromStationName, toStationName]);
    }

    saveBom(forse = false): Promise<boolean> {
        if (!forse && this._historyService.interactiveWindowsService.bomReviewIsOpen()) {
            return Promise.resolve(true);
        }

        const nodes = [];
        this.devicesMap.forEach((devices: BomDeviceDTO[], name: string) =>
            nodes.push({
                name,
                devices
            } as BomNodeDTO)
        );
        let bom;
        if (nodes.length) {
            bom = { nodes } as BomDTO;
        }

        return this._historyService.doCommandBypassHistory(
            this._historyService.command(GtthSetProjectBomCommand).create({
                bomDTO: bom,
                defaultConfig: this.defaultConfig
            } as BomConfigDTO)
        );
    }

    clearBom(): Promise<boolean> {
        if (!this.devicesMap?.size) {
            return Promise.resolve(true);
        }
        this.devicesMap.clear();
        return this.saveBom(true);
    }

    getBomForExport(): BomDTO {
        const nodes = [];
        this.devicesMap.forEach((devices: ExtendedBomDeviceDTO[], name: string) => {
            const mergedDevices = devices.map((device: ExtendedBomDeviceDTO) => this.mergeDeviceConfig(device));

            nodes.push({
                name,
                devices: mergedDevices
            } as BomNodeDTO);
        });
        return { nodes } as BomDTO;
    }

    public getDefaultBomDevices(): ExtendedBomDeviceDTO[] {
        const devices: ExtendedBomDeviceDTO[] = [];

        const used_products = this._historyService.logicalTopology.layersModeEnabled
            ? this._historyService.logicalTopology.selectedProducts
            : [this._historyService.logicalTopology.selectedProducts[0]];

        const productsPerLayer: { product: number; antenna: number; layerIndex: number }[] = [];

        used_products.forEach((selectedProducts: ExtendedProductdata[], layerIndex: number) => {
            selectedProducts.forEach((selectedProduct) => {
                if (isDefined(selectedProduct.usedProd)) {
                    this.putIfAbsent(selectedProduct.usedProd, selectedProduct.usedAnt0, layerIndex, productsPerLayer);
                }
                if (isDefined(selectedProduct.usedProdTU)) {
                    this.putIfAbsent(selectedProduct.usedProdTU, selectedProduct.usedAnt1, layerIndex, productsPerLayer);
                }
            });
        });

        for (const link of this._historyService.logicalTopology.getLinksIterator()) {
            if (link.isActive() && link.getLinkType() !== TopologyLinkTypeEnum.FIBER) {
                if (isDefined(link.getUsedProd0())) {
                    this.putIfAbsent(link.getUsedProd0(), link.getUsedAnt0(), link.getLinkLayerIndex(), productsPerLayer);
                }
                if (isDefined(link.getUsedProd1())) {
                    this.putIfAbsent(link.getUsedProd1(), link.getUsedAnt1(), link.getLinkLayerIndex(), productsPerLayer);
                }
            }
        }

        productsPerLayer.forEach(({ product, antenna, layerIndex }) => {
            this.projectProductsService.getRadiosByBaseId(product).forEach((radio) => {
                devices.push(this.getDefaultDeviceConfig(radio, antenna, layerIndex));
            });
        });

        this.defaultConfig = devices;

        return devices;
    }

    public setDefaultDevicesConfig(devices: ExtendedBomDeviceDTO[]) {
        this.defaultConfig = devices;
    }

    public getDefaultDevicesConfig(): ExtendedBomDeviceDTO[] {
        return this.defaultConfig;
    }

    public getExtendedNodeDevices(activeLayerIndex: number, nodeName: string): ExtendedBomDeviceDTO[] {
        if (!this.devicesMap || this.devicesMap.size === 0) {
            return [];
        }

        return this.devicesMap
            .get(nodeName)
            .filter((deviceDto) => activeLayerIndex === -1 || deviceDto.layerIndex === activeLayerIndex)
            .map((deviceDto) => this.mergeDeviceConfig(deviceDto));
    }

    public getBomNodeNames(activeLayerIndex: number, type?: GtthReportsTabs): string[] {
        const allDevices = [];
        const newDevices = [];
        const installedDevices = [];

        this.devicesMap.forEach((devices: BomDeviceDTO[], name: string) => {
            if (devices.some((device) => (activeLayerIndex === -1 || device.layerIndex === activeLayerIndex) && !device.userForced)) {
                newDevices.push(name);
            }

            if (devices.some((device) => (activeLayerIndex === -1 || device.layerIndex === activeLayerIndex) && device.installed)) {
                installedDevices.push(name);
            }

            if (activeLayerIndex === -1 || devices.some((device) => device.layerIndex === activeLayerIndex)) {
                allDevices.push(name);
            }
        });

        switch (type) {
            case GtthReportsTabs.NEW:
                return newDevices;
            case GtthReportsTabs.INSTALLED:
                return installedDevices;
            default:
                return allDevices;
        }
    }

    checkAssignedNameExists(currentNodeName: string, currentDevice: BomDeviceDTO): boolean {
        const assignedNamesSet = new Set<string>();

        this.devicesMap.forEach((devicesList: BomDeviceDTO[], nodeName: string) => {
            devicesList.forEach((device: BomDeviceDTO) => {
                if (nodeName !== currentNodeName || currentDevice.secondNodeName !== device.secondNodeName) {
                    assignedNamesSet.add(device.deviceAN);
                }
            });
        });
        return assignedNamesSet.has(currentDevice.deviceAN);
    }

    checkIpExists(currentNodeName: string, currentDevice: BomDeviceDTO): boolean {
        const ipAddressSet = new Set<string>();

        this.devicesMap.forEach((devicesList: BomDeviceDTO[], nodeName: string) => {
            devicesList.forEach((device: BomDeviceDTO) => {
                if (nodeName !== currentNodeName || currentDevice.secondNodeName !== device.secondNodeName) {
                    ipAddressSet.add(device.deviceIP);
                }
            });
        });
        return ipAddressSet.has(currentDevice.deviceIP);
    }

    setDeviceIpValue(device: BomDeviceDTO, ipData: DeviceIpViewDataModel, newIpAddress: string): void {
        device.deviceIP = newIpAddress;
        ipData.valueTouched = true;
        ipData.viewValue = newIpAddress;
    }

    getLinkProducts(link: LinkFeature): LinkProduct[] {
        const linkProducts: LinkProduct[] = [];

        if (!this.devicesMap) {
            return [];
        }
        const nodeFrom = link.getFromStation();
        const nodeTo = link.getToStation();

        if (!this.devicesMap.has(nodeFrom.getName()) || !this.devicesMap.has(nodeTo.getName())) {
            return [];
        }
        this.devicesMap.get(nodeFrom.getName()).forEach((device) => {
            if (BomReportUtils.secondaryNodesIncludeNodeName(device.secondNodeName, nodeTo.getName())) {
                linkProducts.push({
                    node: nodeFrom,
                    device,
                    ipData: {
                        viewValue: device.deviceIP,
                        valueTouched: false
                    }
                });
            }
        });
        this.devicesMap.get(nodeTo.getName()).forEach((device) => {
            if (BomReportUtils.secondaryNodesIncludeNodeName(device.secondNodeName, nodeFrom.getName())) {
                linkProducts.push({
                    node: nodeTo,
                    device,
                    ipData: {
                        viewValue: device.deviceIP,
                        valueTouched: false
                    }
                });
            }
        });
        return linkProducts;
    }

    setDeviceRadio(extendedDevice: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        if (extendedDevice.userForced) {
            return;
        }

        extendedDevice.typeProductId = selectedValue.key;
        extendedDevice.typeName = selectedValue.value;

        const newDevice = this.mergeDeviceConfig(extendedDevice);
        extendedDevice.mergeWithDeviceDto(newDevice, extendedDevice.available, true);

        if (this.projectProductsService.isMh(extendedDevice.type) || this.projectProductsService.isMhTgDevice(extendedDevice.type)) {
            // do nothing
        } else if (extendedDevice.deviceType !== ProductDeviceTypeEnum.NONE) {
            const neighbourDevices = this.findNeighbourDevices(currentNodeName, extendedDevice);
            if (neighbourDevices.length === 0) {
                console.error('No neighbours found ' + currentNodeName);
                return;
            }

            const neighbourAvailable = this.projectProductsService.getBomRadios(
                neighbourDevices[0].type,
                neighbourDevices[0].isSectorBu,
                neighbourDevices[0].deviceType
            );

            const selectedIndex = extendedDevice.availableRadios.findIndex((value) => value.key === selectedValue.key);
            if (neighbourAvailable[selectedIndex]) {
                neighbourDevices[0].typeProductId = neighbourAvailable[selectedIndex].key;
                neighbourDevices[0].typeName = neighbourAvailable[selectedIndex].value;
            }
        } else {
            const neighbourDevices = this.findNeighbourDevices(currentNodeName, extendedDevice);
            if (neighbourDevices.length === 0) {
                console.error('No neighbours found ' + currentNodeName);
                return;
            }
            neighbourDevices[0].typeProductId = selectedValue.key;
            neighbourDevices[0].typeName = selectedValue.value;

            if (neighbourDevices.length > 1) {
                console.error(`Found more than one neighbour for ${currentNodeName} device - `, neighbourDevices);
            }
        }
    }

    setDeviceAntenna(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        if (!device.userForced) {
            const selectedAccessory = this.projectProductsService.getAccessoryById(selectedValue.key);
            if (!selectedAccessory) {
                // if selected antenna is N/A, Included or None
                this.updateDeviceAccessory(device, selectedValue, 'antennaAccessoryId', 'antennaName');
                return;
            }

            const activeAccessory = this.projectProductsService.getAccessoryById(device.antennaAccessoryId);
            const currentAntennaType = activeAccessory
                ? this.projectProductsService.getAntennaByBaseId(activeAccessory.baseEntityIds[0]).antennaType
                : EngineAntennaTypeEnum.NORMAL;
            const selectedAntennaType = this.projectProductsService.getAntennaByBaseId(selectedAccessory.baseEntityIds[0]).antennaType;

            if (currentAntennaType !== selectedAntennaType) {
                this.updateDeviceAndNeighbour(device, selectedValue, currentNodeName, 'antennaAccessoryId', 'antennaName');
            } else {
                this.updateDeviceAccessory(device, selectedValue, 'antennaAccessoryId', 'antennaName');
            }

            if (selectedAntennaType === EngineAntennaTypeEnum.DL5) {
                // select ExtendMM for all who use DL-5 antenna
                const availableExtendMM = device.available[ProductAccessoryTypeEnum.OPT_EXTENDMM];
                // enabled checkbox option is always on a second place
                this.updateDeviceAndNeighbour(
                    device,
                    availableExtendMM[availableExtendMM.length - 1],
                    currentNodeName,
                    'extendmmId',
                    'extendmm'
                );
            }
        }
    }

    setDeviceMK(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO) {
        if (!device.userForced) {
            this.updateDeviceAccessory(device, selectedValue, 'mountingKitId', 'mountingKit');
        }
    }

    setDevicePOE(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO) {
        if (!device.userForced) {
            this.updateDeviceAccessory(device, selectedValue, 'poeId', 'poe');
        }
    }

    setDeviceSRG(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO) {
        if (!device.userForced) {
            this.updateDeviceAccessory(device, selectedValue, 'srgId', 'srg');
        }
    }

    isSrgForDeviceDefined(device: ExtendedBomDeviceDTO): boolean {
        return device.srgId >= 0;
    }

    setDeviceSRGQuantity(device: ExtendedBomDeviceDTO, quantity: number) {
        if (device.srgQuantity !== quantity) {
            device.srgQuantity = quantity;
        }
    }

    setDeviceSFP(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO) {
        if (!device.userForced) {
            this.updateDeviceAccessory(device, selectedValue, 'sfpId', 'sfp');
        }
    }

    setDeviceCapacityLicense(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        if (!this.projectProductsService.isMh(device.type) && !this.projectProductsService.isMhTgDevice(device.type)) {
            this.updateDeviceAndNeighbour(device, selectedValue, currentNodeName, 'capacityLicenseId', 'capacityLicense');
        } else {
            const available = device.available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE];
            const selectedIndex = available.findIndex((value) => value.key === selectedValue.key);
            const prevSelectedIndex = available.findIndex((value) => value.key === device.capacityLicenseId);

            if (selectedIndex < prevSelectedIndex) {
                const neighbourDevices = this.findNeighbourDevices(currentNodeName, device);
                neighbourDevices.forEach((neighbour: ExtendedBomDeviceDTO) => {
                    const newCapacity = this.projectProductsService.getBomDeviceConfig(neighbour.typeProductId, neighbour.antenna)
                        .availableList[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE];
                    if (newCapacity[selectedIndex]) {
                        neighbour.capacityLicenseId = newCapacity[selectedIndex].key;
                        neighbour.capacityLicense = newCapacity[selectedIndex].value;
                    }
                });
            }

            this.updateDeviceAccessory(device, selectedValue, 'capacityLicenseId', 'capacityLicense');
        }
    }

    triggerDeviceSector(device: ExtendedBomDeviceDTO, sectorInDevice: number) {
        device.deviceSectorsConfig[sectorInDevice].enabled = !device.deviceSectorsConfig[sectorInDevice].enabled;
    }

    setDeviceSectorLicense(device: ExtendedBomDeviceDTO, sectorInDevice: number, selectedValue: GtthKeyValueDTO) {
        if (!device.userForced) {
            this.updateDeviceSectorAccessory(device, sectorInDevice, selectedValue, 'sectorLicenseId', 'sectorLicense');
        }
    }

    setDeviceSectorCapacityLicense(device: ExtendedBomDeviceDTO, sectorInDevice: number, selectedValue: GtthKeyValueDTO) {
        if (!device.userForced) {
            this.updateDeviceSectorAccessory(device, sectorInDevice, selectedValue, 'capacityLicenseId', 'capacityLicense');
        }
    }

    setDevicePSE(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO) {
        this.updateDeviceAccessory(device, selectedValue, 'pseId', 'pse');
    }

    setDeviceL2(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        this.updateDeviceAndNeighbour(device, selectedValue, currentNodeName, 'l2Id', 'l2');
    }

    setDeviceExtendedMM(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        this.updateDeviceAndNeighbour(device, selectedValue, currentNodeName, 'extendmmId', 'extendmm');
    }

    setDeviceSync(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        this.updateDeviceAndNeighbour(device, selectedValue, currentNodeName, 'syncId', 'sync');
    }

    setDeviceAes(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        this.updateDeviceAndNeighbour(device, selectedValue, currentNodeName, 'aesId', 'aes');
    }

    private checkMhDevicesCollocation(nodesMap: Map<string, BomDeviceDTO[]>) {
        const tu3portRadio = this.projectProductsService.getTu3portRadio();
        if (!tu3portRadio) {
            return;
        }

        nodesMap.forEach((devices: BomDeviceDTO[], _name: string) => {
            const buCount = devices.filter((device) => device.isSectorBu).length;
            if (buCount > 1) {
                devices.forEach((device) => {
                    if (this.projectProductsService.isMh(device.type) && !device.isSectorBu && !device.typeProductId) {
                        device.typeProductId = tu3portRadio.id;
                        device.typeName = tu3portRadio.name;
                    }
                });
            }
        });
    }

    private putIfAbsent(
        product: number,
        antenna: number,
        layerIndex: number,
        productsPerLayer: { product: number; antenna: number; layerIndex: number }[]
    ): void {
        const alreadyExist = productsPerLayer.some(
            (value) => value.product === product && value.antenna === antenna && value.layerIndex === layerIndex
        );
        if (!alreadyExist) {
            productsPerLayer.push({ product, antenna, layerIndex });
        }
    }

    private getDefaultDeviceConfig(radio: ProductRadioDTO, antenna: number, layerIndex: number): ExtendedBomDeviceDTO {
        const predefinedConfig = this.defaultConfig.find(
            (deviceDTO: BomDeviceDTO) =>
                deviceDTO.typeProductId === radio.id && deviceDTO.antenna === antenna && deviceDTO.layerIndex === layerIndex
        );

        const productConfig: BomDeviceConfigModel = this.projectProductsService.getBomDeviceConfig(radio.id, antenna);

        let device: ExtendedBomDeviceDTO;
        if (predefinedConfig) {
            device = ExtendedBomDeviceDTO.fromDeviceDto(predefinedConfig);
            device.mergeWithDeviceDto(predefinedConfig, productConfig.availableList, false);
        } else {
            device = ExtendedBomDeviceDTO.empty();
            device.type = radio.baseEntityId;
            device.typeName = radio.name;
            device.typeProductId = radio.id;

            device.antenna = antenna;
            productConfig.setValuesToBomDevice(device);
        }

        device.deviceType = radio.deviceType;
        device.isSectorBu = radio.mhType === ProductMhTypeEnum.BU;
        device.available = productConfig.availableList;
        device.layerIndex = layerIndex;
        if (!isDefined(device.srgQuantity)) {
            device.srgQuantity = 1;
        }
        // select ExtendMM for all who use DL-5 antenna
        const selectedAntenna = this.projectProductsService.getAntennaByBaseId(antenna);
        if (selectedAntenna && selectedAntenna.antennaType === EngineAntennaTypeEnum.DL5) {
            const availableExtendMM = device.available[ProductAccessoryTypeEnum.OPT_EXTENDMM];
            //enabled checkbox option is always on a second place
            device.extendmmId = availableExtendMM[availableExtendMM.length - 1].key;
            device.extendmm = availableExtendMM[availableExtendMM.length - 1].value;
        }

        return device;
    }

    private mergeDeviceConfig(extendedConfig: ExtendedBomDeviceDTO): ExtendedBomDeviceDTO {
        const radios = this.projectProductsService.getBomRadios(extendedConfig.type, extendedConfig.isSectorBu, extendedConfig.deviceType);

        const productConfig: BomDeviceConfigModel = this.projectProductsService.getBomDeviceConfig(
            extendedConfig.typeProductId,
            extendedConfig.antenna
        );
        extendedConfig.availableRadios = radios;
        extendedConfig.available = productConfig.availableList;

        const defaultConfig = this.getDefaultBomDevices().find(
            (deviceDTO: BomDeviceDTO) =>
                deviceDTO.typeProductId === extendedConfig.typeProductId &&
                deviceDTO.antenna === extendedConfig.antenna &&
                deviceDTO.layerIndex === extendedConfig.layerIndex
        );

        if (defaultConfig) {
            extendedConfig.mergeWithDeviceDto(defaultConfig, productConfig.availableList, false);
        } else {
            productConfig.setValuesToBomDevice(extendedConfig);
        }

        return extendedConfig;
    }

    private findNeighbourDevices(currentNodeName: string, device: BomDeviceDTO): BomDeviceDTO[] {
        const result = [];
        const neighbourNames = device.secondNodeName.split(', ');
        neighbourNames.forEach((neighbourName: string) => {
            const neighbourNodeDevices = this.devicesMap.get(neighbourName);
            neighbourNodeDevices.forEach((neighbourDevice: BomDeviceDTO) => {
                if (BomReportUtils.secondaryNodesIncludeNodeName(neighbourDevice.secondNodeName, currentNodeName)) {
                    result.push(neighbourDevice);
                }
            });
        });

        return result;
    }

    private updateDeviceAccessory(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, idField: string, valueField: string) {
        device[idField] = selectedValue.key;
        device[valueField] = selectedValue.value;
    }

    private updateDeviceSectorAccessory(
        device: ExtendedBomDeviceDTO,
        sectorIdx: number,
        selectedValue: GtthKeyValueDTO,
        idField: string,
        valueField: string
    ) {
        device.deviceSectorsConfig[sectorIdx][idField] = selectedValue.key;
        device.deviceSectorsConfig[sectorIdx][valueField] = selectedValue.value;
    }

    private updateDeviceAndNeighbour(
        device: ExtendedBomDeviceDTO,
        selectedValue: GtthKeyValueDTO,
        currentNodeName: string,
        idField: string,
        valueField: string
    ) {
        if (this.projectProductsService.isMh(device.type) || this.projectProductsService.isMhTgDevice(device.type)) {
            return;
        }

        this.updateDeviceAccessory(device, selectedValue, idField, valueField);

        const neighbourDevices = this.findNeighbourDevices(currentNodeName, device);
        neighbourDevices.forEach((neightbour: BomDeviceDTO) => {
            if (neightbour) {
                neightbour[idField] = selectedValue.key;
                neightbour[valueField] = selectedValue.value;
            }
        });
    }
}
